import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from './firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { MdFileUpload, MdPerson, MdLock, MdCloudDone, MdBusiness, MdClose, MdCheck } from 'react-icons/md'; 
import Cropper from 'react-easy-crop'; // Requirement: npm install react-easy-crop
import logo from './logo.png';

import maleProfileDefaultImage from './bdp.jpg'; 
import femaleProfileDefaultImage from './gdp.jpg'; 
import defaultCoverImage from './coverimage1.jpg'; 

const primaryColor = '#4A90E2';
const secondaryColor = '#50E3C2';
const successColor = '#4CAF50';
const errorColor = '#FF5252';
const backgroundColor = '#F7F9FC';
const cardShadow = '0 6px 16px rgba(0, 0, 0, 0.08)';

// --- Styled Components ---
const PageContainer = styled.div`
  display: flex; justify-content: center; align-items: center; min-height: 100vh;
  background-color: ${backgroundColor}; padding: 30px;
`;

const AuthCard = styled.div`
  background-color: #fff; padding: 40px 60px; border-radius: 12px;
  box-shadow: ${cardShadow}; width: 100%; max-width: 800px; text-align: center;
`;

const StepContentWrapper = styled.div` width: 100%; display: flex; flex-direction: column; align-items: center; `;
const Logo = styled.img` width: 60px; height: 60px; margin-bottom: 20px; `;
const WelcomeText = styled.h2` font-size: 28px; font-weight: 700; color: #333; margin-bottom: 8px; `;
const Label = styled.label` display: block; font-size: 15px; color: #555; margin-bottom: 8px; font-weight: 600; text-align: left; &.required::after { content: ' *'; color: ${errorColor}; } `;
const InputStyle = css` width: 100%; padding: 12px 15px; border-radius: 8px; border: 1px solid #e0e0e0; font-size: 16px; &:focus { border-color: ${primaryColor}; outline: none; } `;
const Input = styled.input`${InputStyle}`;
const Select = styled.select`${InputStyle}`;
const TextArea = styled.textarea`${InputStyle} min-height: 100px; resize: vertical;`;
const Title = styled.h2` font-size: 24px; font-weight: 700; color: #333; margin-bottom: 30px; border-bottom: 2px solid ${secondaryColor}; display: inline-block; `;
const FormGrid = styled.div` display: grid; grid-template-columns: 1fr; gap: 25px; width: 100%; @media (min-width: 600px) { grid-template-columns: repeat(2, 1fr); } `;
const FormField = styled.div` margin-bottom: 15px; `;
const Message = styled.p` font-size: 16px; margin-top: 25px; padding: 10px 15px; border-radius: 6px; color: ${props => props.type === 'error' ? errorColor : successColor}; background-color: ${props => props.type === 'error' ? 'rgba(255, 82, 82, 0.1)' : 'rgba(76, 175, 80, 0.1)'}; `;
const ButtonGroup = styled.div` display: flex; justify-content: space-between; margin-top: 40px; gap: 20px; `;
const Button = styled.button` background-color: ${props => props.$primary ? primaryColor : '#AAB8C2'}; color: white; padding: 12px 25px; border-radius: 8px; border: none; font-size: 16px; cursor: pointer; flex: 1; &:disabled { background-color: #BCCCDC; } `;
const FileUploadContainer = styled.div` border: 2px dashed #d0d0d0; padding: 30px; text-align: center; cursor: pointer; border-radius: 10px; `;
const PreviewImage = styled.img` width: 100px; height: 100px; border-radius: 50%; object-fit: cover; `;
const CoverImagePreview = styled.img` width: 100%; height: 200px; object-fit: cover; border-radius: 8px; `;

const ProgressContainer = styled.div` display: flex; justify-content: space-between; margin-bottom: 50px; position: relative; &:before { content: ''; position: absolute; width: 100%; height: 4px; background-color: #E0E6ED; top: 25px; z-index: 1; } `;
const ProgressBar = styled.div` position: absolute; height: 4px; background-color: ${primaryColor}; top: 25px; left: 0; width: ${props => ((props.$currentStep - 1) / (props.$totalSteps - 1)) * 100}%; transition: width 0.4s ease; z-index: 2; `;
const StepItem = styled.div` display: flex; flex-direction: column; align-items: center; position: relative; z-index: 3; `;
const StepCircle = styled.div` width: 50px; height: 50px; border-radius: 50%; background-color: ${props => props.$active || props.$completed ? primaryColor : '#E0E6ED'}; color: white; display: flex; justify-content: center; align-items: center; border: 4px solid #fff; `;
const ReviewTable = styled.table` width: 100%; border-collapse: collapse; text-align: left; `;
const ReviewRow = styled.tr` border-bottom: 1px solid #eee; `;

// --- Cropper Styled Components ---
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8);
  display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 5000;
`;
const CropperContainer = styled.div`
  position: relative; width: 90%; max-width: 600px; height: 400px; background: #333; border-radius: 8px; overflow: hidden;
`;

// --- Image Cropping Helper ---
const getCroppedImg = (imageSrc, pixelCrop) => {
    const canvas = document.createElement('canvas');
    const image = new Image();
    image.src = imageSrc;
    return new Promise((resolve) => {
        image.onload = () => {
            canvas.width = pixelCrop.width;
            canvas.height = pixelCrop.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                0, 0, pixelCrop.width, pixelCrop.height
            );
            canvas.toBlob((blob) => {
                const file = new File([blob], "cropped_cover.jpg", { type: "image/jpeg" });
                resolve({ file, url: URL.createObjectURL(blob) });
            }, 'image/jpeg');
        };
    });
};

const steps = [
    { id: 1, label: 'Role Selection', Icon: MdPerson },
    { id: 2, label: 'Contact Info', Icon: MdLock },
    { id: 3, label: 'Visuals', Icon: MdFileUpload },
    { id: 4, label: 'Professional Info', Icon: MdBusiness }, 
    { id: 5, label: 'Finish', Icon: MdCloudDone },
];

const renderStepContent = (currentStep, formData, handleChange, handleFileChange, triggerCoverPicker) => {
    const mainRoleLower = formData.mainRole.toLowerCase();
    switch (currentStep) {
        case 1:
            return (
                <FormGrid>
                    <FormField><Label className="required">Full Name</Label><Input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Enter your full name" /></FormField>
                    <FormField><Label className="required">Primary Role</Label>
                        <Select name="mainRole" value={formData.mainRole} onChange={handleChange}>
                            <option value="">Select Role</option>
                            <option value="Business Owner">Business Owner</option>
                            <option value="Manager">Manager</option>
                            <option value="Employee">Employee</option>
                            <option value="Student">Student (College/School)</option>
                            <option value="Public">Public User</option>
                        </Select>
                    </FormField>
                    <FormField><Label>Date of Birth (Optional)</Label><Input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} /></FormField>
                    <FormField><Label className="required">Gender</Label><Select name="gender" value={formData.gender} onChange={handleChange}><option value="">Select Gender</option><option value="male">Male</option><option value="female">Female</option></Select></FormField>
                </FormGrid>
            );
        case 2:
            return (
                <FormGrid>
                    <FormField style={{ gridColumn: 'span 2' }}><Label className="required">Address</Label><TextArea name="address" value={formData.address} onChange={handleChange} placeholder="House No, Street, Area..." /></FormField>
                    <FormField>
                        <Label className="required">Select Your Country</Label>
                        <Select name="country" value={formData.country} onChange={handleChange}>
                            <option value="">Select Country</option>
                            <option value="India">India</option>
                            <option value="USA">USA</option>
                            <option value="UK">UK</option>
                            <option value="Sri Lanka">Sri Lanka</option>
                        </Select>
                    </FormField>
                    <FormField>
                        <Label className="required">Select Your State</Label>
                        <Select name="state" value={formData.state} onChange={handleChange}>
                            <option value="">Select State</option>
                            <option value="Tamil Nadu">Tamil Nadu</option>
                            <option value="Kerala">Kerala</option>
                            <option value="Karnataka">Karnataka</option>
                            <option value="Andhra Pradesh">Andhra Pradesh</option>
                            <option value="Others">Others</option>
                        </Select>
                    </FormField>
                    <FormField>
                        <Label className="required">Enter Your City</Label>
                        <Input name="city" value={formData.city} onChange={handleChange} placeholder="e.g. Chennai or Kovilpatti" />
                    </FormField>
                    <FormField><Label className="required">Pincode</Label><Input name="pincode" value={formData.pincode} onChange={handleChange} placeholder="6-digit Pincode" /></FormField>
                    <FormField style={{ gridColumn: 'span 2' }}><Label className="required">Phone Number</Label><Input name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter mobile number" /></FormField>
                </FormGrid>
            );
        case 3:
            return (
                <FormGrid style={{ gridTemplateColumns: '1fr' }}>
                    <FileUploadContainer onClick={() => document.getElementById('profileImageFile').click()}>
                        <MdFileUpload size={30} /><p>Profile Picture</p>
                        <input type="file" id="profileImageFile" name="profileImageFile" style={{display: 'none'}} onChange={handleFileChange} accept="image/*" />
                        {formData.profileImageUrl && <PreviewImage src={formData.profileImageUrl} />}
                    </FileUploadContainer>
                    <FileUploadContainer onClick={triggerCoverPicker}>
                        <MdFileUpload size={30} /><p>Cover Photo (Will open Cropper)</p>
                        <input type="file" id="coverImageFileRaw" style={{display: 'none'}} onChange={handleFileChange} accept="image/*" />
                        {formData.coverImageUrl && <CoverImagePreview src={formData.coverImageUrl} />}
                    </FileUploadContainer>
                </FormGrid>
            );
        case 4:
            if (mainRoleLower === 'business owner') {
                return (
                    <FormGrid>
                        <FormField style={{ gridColumn: 'span 2' }}><Label className="required">Business Name</Label><Input name="businessName" value={formData.businessName} onChange={handleChange} /></FormField>
                        <FormField><Label className="required">Industry</Label><Input name="businessIndustry" value={formData.businessIndustry} onChange={handleChange} /></FormField>
                    </FormGrid>
                );
            } else if (mainRoleLower === 'manager') {
                return (
                    <FormGrid>
                        <FormField style={{ gridColumn: 'span 2' }}><Label className="required">Company Name</Label><Input name="managerCompanyName" value={formData.managerCompanyName} onChange={handleChange} placeholder="Enter your company name" /></FormField>
                        <FormField style={{ gridColumn: 'span 2' }}><Label className="required">Department</Label><Input name="managerDepartment" value={formData.managerDepartment} onChange={handleChange} placeholder="e.g. IT, HR, Sales" /></FormField>
                    </FormGrid>
                );
            } else if (mainRoleLower === 'student') {
                return (
                    <FormGrid>
                        <FormField style={{ gridColumn: 'span 2' }}>
                            <Label className="required">School / College Name</Label>
                            <Input name="institutionName" value={formData.institutionName} onChange={handleChange} placeholder="e.g. Kamaraj Matric School" />
                        </FormField>
                        <FormField>
                            <Label className="required">Current Class / Group</Label>
                            <Input name="fieldOfStudy" value={formData.fieldOfStudy} onChange={handleChange} placeholder="e.g. 12th Std (Maths-Bio)" />
                        </FormField>
                        <FormField>
                            <Label className="required">Job Preference</Label>
                            <Select name="workPreference" value={formData.workPreference} onChange={handleChange}>
                                <option value="">Select Option</option>
                                <option value="Part-time">Part-time (After School)</option>
                                <option value="Full-time">Full-time</option>
                                <option value="Remote">Remote / Online Work</option>
                                <option value="Looking for Internship">Looking for Internship</option>
                            </Select>
                        </FormField>
                    </FormGrid>
                );
            }
            return (
                <FormGrid>
                    <FormField style={{ gridColumn: 'span 2' }}><Label className="required">Occupation</Label><Input name="publicOccupation" value={formData.publicOccupation} onChange={handleChange} /></FormField>
                    <FormField style={{ gridColumn: 'span 2' }}><Label>Bio</Label><TextArea name="publicBio" value={formData.publicBio} onChange={handleChange} /></FormField>
                </FormGrid>
            );
        case 5:
            return (
                <StepContentWrapper>
                    <Title>Review Details</Title>
                    <ReviewTable>
                        <tbody>
                            <ReviewRow><th>Name:</th><td>{formData.fullName}</td></ReviewRow>
                            <ReviewRow><th>Role:</th><td>{formData.mainRole}</td></ReviewRow>
                            <ReviewRow><th>Location:</th><td>{formData.city}, {formData.state}, {formData.country}</td></ReviewRow>
                            {mainRoleLower === 'student' && (
                                <>
                                    <ReviewRow><th>School/College:</th><td>{formData.institutionName}</td></ReviewRow>
                                    <ReviewRow><th>Class/Field:</th><td>{formData.fieldOfStudy}</td></ReviewRow>
                                    <ReviewRow><th>Preference:</th><td>{formData.workPreference}</td></ReviewRow>
                                </>
                            )}
                            {mainRoleLower === 'manager' && (
                                <>
                                    <ReviewRow><th>Company:</th><td>{formData.managerCompanyName}</td></ReviewRow>
                                    <ReviewRow><th>Department:</th><td>{formData.managerDepartment}</td></ReviewRow>
                                </>
                            )}
                        </tbody>
                    </ReviewTable>
                </StepContentWrapper>
            );
        default: return null;
    }
};

function CreateProfile() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const totalSteps = steps.length;

    // Form States
    const [formData, setFormData] = useState({
        fullName: '', mainRole: '', dateOfBirth: '', gender: '',
        address: '', city: '', state: '', country: '', phone: '', pincode: '',
        profileImageFile: null, profileImageUrl: '',
        coverImageFile: null, coverImageUrl: '',
        businessName: '', businessIndustry: '', 
        managerCompanyName: '', managerDepartment: '',
        publicOccupation: '', publicBio: '',
        institutionName: '', fieldOfStudy: '', workPreference: '' 
    });

    // Cropper States
    const [cropImage, setCropImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (u) => {
            if (u) {
                setUser(u);
                const fullEmailId = u.email; 
                const emailSnap = await getDoc(doc(db, "profiles", fullEmailId));
                if (emailSnap.exists()) { navigate('/home'); return; }
                setUsername(fullEmailId);
            } else {
                navigate('/login');
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (e.target.id === 'coverImageFileRaw') {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => setCropImage(reader.result);
        } else {
            setFormData({ ...formData, [e.target.name]: file, [e.target.name.replace('File', 'Url')]: URL.createObjectURL(file) });
        }
    };

    const onCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const saveCroppedCover = async () => {
        try {
            const { file, url } = await getCroppedImg(cropImage, croppedAreaPixels);
            setFormData({ ...formData, coverImageFile: file, coverImageUrl: url });
            setCropImage(null); 
        } catch (e) {
            console.error(e);
        }
    };

    const uploadFile = async (file, path) => {
        if (!file) return null;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const isStepValid = () => {
        const { fullName, mainRole, gender, address, city, state, country, phone, pincode, businessName, managerCompanyName, managerDepartment, publicOccupation, institutionName, fieldOfStudy, workPreference } = formData;
        if (currentStep === 1) return fullName && mainRole && gender; 
        if (currentStep === 2) return address && city && state && country && phone && pincode;
        if (currentStep === 4) {
            if (mainRole.toLowerCase() === 'business owner') return businessName;
            if (mainRole.toLowerCase() === 'manager') return managerCompanyName && managerDepartment;
            if (mainRole.toLowerCase() === 'student') return institutionName && fieldOfStudy && workPreference;
            return publicOccupation;
        }
        return true;
    };

    const handleNext = () => {
        if (!isStepValid()) { setMessage({ type: 'error', text: 'Please fill all required fields!' }); return; }
        setMessage({ type: '', text: '' });
        if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
        else handleSubmit();
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            let pUrl = formData.profileImageUrl || (formData.gender === 'female' ? femaleProfileDefaultImage : maleProfileDefaultImage);
            let cUrl = formData.coverImageUrl || defaultCoverImage;

            if (formData.profileImageFile) pUrl = await uploadFile(formData.profileImageFile, `profiles/${user.uid}/pfp_${Date.now()}`);
            if (formData.coverImageFile) cUrl = await uploadFile(formData.coverImageFile, `profiles/${user.uid}/cover_${Date.now()}`);

            const finalData = {
                ...formData,
                profileImageUrl: pUrl,
                coverImageUrl: cUrl,
                uid: user.uid,
                email: user.email,
                username: username,
                createdAt: new Date(),
                profileImageFile: null,
                coverImageFile: null
            };

            await setDoc(doc(db, "profiles", username), finalData);
            navigate(`/home`); 
        } catch (e) { 
            console.error(e);
            setMessage({ type: 'error', text: 'Error saving profile: ' + e.message }); 
        }
        setLoading(false);
    };

    return (
        <PageContainer>
            <AuthCard>
                <Logo src={logo} />
                <WelcomeText>Create Your Profile</WelcomeText>
                <ProgressContainer>
                    <ProgressBar $currentStep={currentStep} $totalSteps={totalSteps} />
                    {steps.map(s => (
                        <StepItem key={s.id}>
                            <StepCircle $active={currentStep === s.id} $completed={currentStep > s.id}><s.Icon /></StepCircle>
                        </StepItem>
                    ))}
                </ProgressContainer>

                {renderStepContent(currentStep, formData, handleChange, handleFileChange, () => document.getElementById('coverImageFileRaw').click())}

                <ButtonGroup>
                    {currentStep > 1 && <Button onClick={() => setCurrentStep(currentStep - 1)}>Back</Button>}
                    <Button $primary onClick={handleNext} disabled={loading}>{loading ? 'Processing...' : currentStep === totalSteps ? 'Complete' : 'Next'}</Button>
                </ButtonGroup>
                {message.text && <Message type={message.type}>{message.text}</Message>}
            </AuthCard>

            {cropImage && (
                <ModalOverlay>
                    <h3 style={{color:'white', marginBottom:'15px'}}>Crop Cover Photo (16:9)</h3>
                    <CropperContainer>
                        <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={16 / 9}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </CropperContainer>
                    <div style={{marginTop: '20px', display:'flex', gap:'20px'}}>
                        <Button onClick={() => setCropImage(null)} style={{background:'#555'}}><MdClose /> Cancel</Button>
                        <Button $primary onClick={saveCroppedCover}><MdCheck /> Apply Crop</Button>
                    </div>
                </ModalOverlay>
            )}
        </PageContainer>
    );
}

export default CreateProfile;